/**
 * Stage 7 — the customer-support POLICY, as a pure module with no imports.
 *
 * Split out of `customer-support.tsx` on purpose: `hubspot-initializer.tsx`
 * needs the policy, and `customer-support.tsx` needs the initializer's
 * `useHubSpot`. Keeping the policy import-free is what stops that from
 * becoming a module cycle.
 *
 * ── Failure direction (load-bearing) ──────────────────────────────────────
 * Support is a TRUE-default capability. A missing field, a missing current
 * space, and a malformed carrier ALL resolve to ENABLED. Only an explicit
 * `false` hides actionable support. Getting this backwards would strip the
 * support contact from every Live Mindspace, so the rule below is written as
 * "disabled iff the value is literally `false`" — never `Boolean(value)` and
 * never `value ?? false`.
 */

/** The single support address this product exposes to learners. */
export const CUSTOMER_SUPPORT_EMAIL = "hello@mindstone.com";

/**
 * The generated `SpaceMetadataFragment` gains `customerSupportEnabled` under
 * `modules` in Stage 1. Reading it through this deliberately narrow shape
 * keeps the seam compiling on both sides of that merge: an older carrier
 * simply omits the key, and omission is the capability default.
 *
 * `modules` is optional on purpose. `MainContext` hands out a hand-written
 * default space during the first render and on unauthenticated routes;
 * treating that as "no opinion" (rather than "disabled") is what keeps the
 * fail direction correct.
 *
 * `__typename` is declared only as a structural anchor. TypeScript's weak-type
 * check rejects an assignment to an all-optional type that shares NO property
 * with the source, so without it `SpaceMetadataFragment["modules"]` would stop
 * being assignable here until Stage 1 adds the real field.
 */
export type CustomerSupportCarrier =
  | {
      readonly modules?: {
        readonly __typename?: string;
        readonly customerSupportEnabled?: boolean | null;
      } | null;
    }
  | null
  | undefined;

/**
 * The whole policy, as a pure function so the contract can be tested without a
 * React tree.
 *
 * Returns `false` only for an explicit `false`. Everything else — `true`,
 * `null`, `undefined`, a missing `modules` object, a missing space — is
 * support ENABLED.
 */
export function resolveCustomerSupport(space: CustomerSupportCarrier): boolean {
  return space?.modules?.customerSupportEnabled !== false;
}
